import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../components/SubscriptionGuard';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, addWeeks, subWeeks, parseISO, isSameWeek, isToday, isBefore, startOfDay } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Lock,
  Send,
  Download,
  Calendar,
  FileText,
  History,
  ChevronDown,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  RefreshCw,
  Pencil
} from 'lucide-react';
import MonthlyView from '../components/timesheet/MonthlyView';
import { useTranslation } from 'react-i18next';

const Timesheet = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const { subscription } = useSubscription() || {};
  const isFreePlan = subscription?.plan === 'free';
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const monday = startOfWeek(today, { weekStartsOn: 1 });
    return format(monday, 'yyyy-MM-dd');
  });
  
  // Timesheet data
  const [timesheet, setTimesheet] = useState(null);
  const [weekDates, setWeekDates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [hours, setHours] = useState({});
  const [history, setHistory] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [workspaceSettings, setWorkspaceSettings] = useState(null);
  
  // UI state
  const [viewMode, setViewMode] = useState('weekly'); // weekly, monthly
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showHistorySection, setShowHistorySection] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [cellValue, setCellValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT') return;
      
      if (e.key === 'ArrowLeft') {
        navigateWeek(-1);
      } else if (e.key === 'ArrowRight') {
        navigateWeek(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentWeekStart]);

  // Fetch timesheet data
  useEffect(() => {
    fetchTimesheet();
  }, [currentWeekStart, api]);

  const fetchTimesheet = async () => {
    if (!api) return;
    setLoading(true);
    try {
      const res = await api.get(`/timesheets/week/${currentWeekStart}`);
      setTimesheet(res.data.timesheet);
      setWeekDates(res.data.week_dates);
      setProjects(res.data.projects);
      setHours(res.data.hours);
      
      // Fetch holidays for the week
      try {
        const holidayRes = await api.get('/holidays');
        setHolidays(holidayRes.data || []);
      } catch (err) {
        console.error('Error fetching holidays:', err);
      }
      
      // Fetch workspace settings for submission deadline
      try {
        const wsRes = await api.get('/workspace-settings');
        setWorkspaceSettings(wsRes.data);
      } catch (err) {
        console.error('Error fetching workspace settings:', err);
      }
    } catch (error) {
      console.error('Failed to fetch timesheet:', error);
      toast.error('Failed to load timesheet');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    if (!api) return;
    try {
      const res = await api.get('/timesheets/history');
      setHistory(res.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  useEffect(() => {
    if (showHistorySection) {
      fetchHistory();
    }
  }, [showHistorySection, api]);

  const navigateWeek = (direction) => {
    const current = parseISO(currentWeekStart);
    const newDate = direction > 0 ? addWeeks(current, 1) : subWeeks(current, 1);
    setCurrentWeekStart(format(newDate, 'yyyy-MM-dd'));
  };

  const goToToday = () => {
    const today = new Date();
    const monday = startOfWeek(today, { weekStartsOn: 1 });
    setCurrentWeekStart(format(monday, 'yyyy-MM-dd'));
  };

  const isCurrentWeek = useMemo(() => {
    const today = new Date();
    const currentMonday = parseISO(currentWeekStart);
    return isSameWeek(today, currentMonday, { weekStartsOn: 1 });
  }, [currentWeekStart]);

  // Get hours for a cell
  const getCellHours = (projectId, date) => {
    const key = `${projectId}_${date}`;
    return hours[key] || 0;
  };

  // Calculate daily totals
  const dailyTotals = useMemo(() => {
    const totals = {};
    weekDates.forEach(date => {
      totals[date] = projects.reduce((sum, p) => {
        return sum + getCellHours(p.id, date);
      }, 0);
      // Also add "no-project" hours
      totals[date] += getCellHours('no-project', date);
    });
    return totals;
  }, [hours, weekDates, projects]);

  // Calculate project totals
  const projectTotals = useMemo(() => {
    const totals = {};
    projects.forEach(p => {
      totals[p.id] = weekDates.reduce((sum, date) => {
        return sum + getCellHours(p.id, date);
      }, 0);
    });
    // No project total
    totals['no-project'] = weekDates.reduce((sum, date) => {
      return sum + getCellHours('no-project', date);
    }, 0);
    return totals;
  }, [hours, weekDates, projects]);

  // Grand total
  const grandTotal = useMemo(() => {
    return Object.values(dailyTotals).reduce((sum, h) => sum + h, 0);
  }, [dailyTotals]);

  // Check if timesheet is editable
  const isEditable = timesheet?.status === 'draft' || timesheet?.status === 'rejected';

  // Check if submission is allowed based on day-of-week rules
  // Current week: only Fri, Sat, Sun; Previous weeks: always allowed
  const canSubmitTimesheet = useMemo(() => {
    if (!isEditable) return { allowed: false, reason: '' };
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const currentMonday = startOfWeek(today, { weekStartsOn: 1 });
    const currentWeekStr = format(currentMonday, 'yyyy-MM-dd');
    
    // Dynamic deadline from workspace settings
    const DAY_MAP = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
    const deadlineDayStr = workspaceSettings?.submission_deadline_day || 'friday';
    const deadlineDayNum = DAY_MAP[deadlineDayStr] ?? 5;
    const dayName = deadlineDayStr.charAt(0).toUpperCase() + deadlineDayStr.slice(1);
    
    if (currentWeekStart === currentWeekStr) {
      // Current week - allow submission from deadline day onwards
      const todayOrder = dayOfWeek === 0 ? 7 : dayOfWeek;
      const deadlineOrder = deadlineDayNum === 0 ? 7 : deadlineDayNum;
      
      if (todayOrder < deadlineOrder) {
        const daysUntilDeadline = deadlineOrder - todayOrder;
        return { 
          allowed: false, 
          reason: `Current week's timesheet can only be submitted from ${dayName} onwards. ${daysUntilDeadline} day${daysUntilDeadline > 1 ? 's' : ''} until ${dayName}.`
        };
      }
    } else if (currentWeekStart > currentWeekStr) {
      return { allowed: false, reason: 'Cannot submit a timesheet for a future week.' };
    }
    // Previous weeks: always allowed
    return { allowed: true, reason: '' };
  }, [currentWeekStart, isEditable, workspaceSettings]);

  // Check if a date is a weekend (Saturday=6, Sunday=0)
  const isWeekendDate = (dateStr) => {
    const date = parseISO(dateStr);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  // Check if a date is today
  const isTodayDate = (dateStr) => {
    return isToday(parseISO(dateStr));
  };

  // Check if a date is in the future
  const isFutureDate = (dateStr) => {
    const date = startOfDay(parseISO(dateStr));
    const today = startOfDay(new Date());
    return date > today;
  };

  // Check if a date is a past working day (not today, not future, not weekend)
  const isPastWorkingDay = (dateStr) => {
    const date = startOfDay(parseISO(dateStr));
    const today = startOfDay(new Date());
    return date < today && !isWeekendDate(dateStr);
  };

  // Get holiday for a specific date
  const getHolidayForDate = (dateStr) => {
    return holidays.find(h => h.date === dateStr);
  };

  // Check if cell is editable (not weekend, not holiday)
  const isCellEditable = (dateStr) => {
    if (!isEditable) return false;
    if (isWeekendDate(dateStr)) return false;
    if (getHolidayForDate(dateStr)) return false;
    return true;
  };

  // Handle cell click
  const handleCellClick = (projectId, date) => {
    if (!isCellEditable(date)) return;
    setEditingCell({ projectId, date });
    const currentHours = getCellHours(projectId, date);
    // Show empty for 0 hours, or the numeric value for editing
    setCellValue(currentHours > 0 ? currentHours.toString() : '');
  };

  // Handle cell save
  const handleCellSave = async () => {
    if (!editingCell || !timesheet) return;

    const numValue = parseFloat(cellValue) || 0;
    if (numValue < 0 || numValue > 24) {
      toast.error('Hours must be between 0 and 24');
      return;
    }

    try {
      await api.put(`/timesheets/${timesheet.id}/cell`, {
        project_id: editingCell.projectId,
        date: editingCell.date,
        hours: numValue
      });

      // Update local state
      const key = `${editingCell.projectId}_${editingCell.date}`;
      setHours(prev => ({ ...prev, [key]: numValue }));
      setEditingCell(null);
    } catch (error) {
      toast.error('Failed to save hours');
    }
  };

  // Handle key press in cell input
  const handleCellKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCellSave();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      handleCellSave();
      // Move to next cell (simplified)
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!timesheet) return;
    setSubmitting(true);
    try {
      await api.post(`/timesheets/${timesheet.id}/submit`);
      toast.success('Timesheet submitted for approval');
      setShowSubmitDialog(false);
      fetchTimesheet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle export
  const handleExport = async (exportFormat) => {
    if (!timesheet?.id) return;
    try {
      if (exportFormat === 'csv') {
        const response = await api.get(`/timesheets/${timesheet.id}/export?format=csv`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `timesheet-${currentWeekStart}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const response = await api.get(`/timesheets/${timesheet.id}/export?format=json`);
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `timesheet-${currentWeekStart}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      toast.success('Timesheet exported');
    } catch (error) {
      toast.error('Failed to export timesheet');
    }
  };

  // Status badge component
  const StatusBadge = ({ status, large = false }) => {
    const config = {
      draft: { icon: Clock, label: t('timesheet.draft'), class: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 dark:bg-slate-700 dark:text-slate-300' },
      submitted: { icon: Send, label: t('timesheet.submitted'), class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300' },
      approved: { icon: CheckCircle2, label: t('timesheet.approved'), class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' },
      rejected: { icon: XCircle, label: t('timesheet.rejected'), class: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300' }
    };

    const { icon: Icon, label, class: className } = config[status] || config.draft;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-medium ${className} ${large ? 'text-sm' : 'text-xs'}`}>
        <Icon className={large ? 'w-4 h-4' : 'w-3 h-3'} />
        {label}
        {status !== 'draft' && status !== 'rejected' && <Lock className={large ? 'w-3.5 h-3.5 ml-1' : 'w-3 h-3 ml-0.5'} />}
      </span>
    );
  };

  // Format hours display - always "Xh Xm" format
  const formatHours = (h) => {
    if (h === 0 || h === null || h === undefined) return '-';
    const hours = Math.floor(h);
    const minutes = Math.round((h - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  // Format hours for grand total (always show numeric value)
  const formatGrandTotal = (h) => {
    const hours = Math.floor(h);
    const minutes = Math.round((h - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  // Get cell display class based on date status
  const getCellBackgroundClass = (dateStr, hasHours) => {
    const weekend = isWeekendDate(dateStr);
    const holiday = getHolidayForDate(dateStr);
    const today = isTodayDate(dateStr);
    const future = isFutureDate(dateStr);
    const pastWorking = isPastWorkingDay(dateStr) && !holiday;
    
    if (weekend) return 'bg-slate-100 dark:bg-slate-700/60 dark:bg-slate-800/40';
    if (holiday) return 'bg-red-50 dark:bg-red-900/30/60 dark:bg-red-900/5';
    if (today) return 'bg-blue-50 dark:bg-blue-900/30/60 dark:bg-blue-900/10';
    if (future) return 'bg-slate-50 dark:bg-slate-700/30 dark:bg-slate-800/20';
    if (pastWorking && !hasHours) return 'bg-amber-50 dark:bg-amber-900/30/60 dark:bg-amber-900/5';
    return '';
  };

  // Get header column class
  const getHeaderClass = (dateStr) => {
    const weekend = isWeekendDate(dateStr);
    const holiday = getHolidayForDate(dateStr);
    const today = isTodayDate(dateStr);
    const future = isFutureDate(dateStr);
    const pastWorking = isPastWorkingDay(dateStr) && !holiday;
    const hasZeroHours = dailyTotals[dateStr] === 0;
    
    if (weekend) return 'bg-slate-100 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500';
    if (holiday) return 'bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400';
    if (today) return 'bg-blue-50 dark:bg-blue-900/10 text-blue-600 dark:text-blue-400';
    if (future) return 'text-slate-400 dark:text-slate-500';
    if (pastWorking && hasZeroHours) return 'bg-amber-50 dark:bg-amber-900/30/80 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400';
    return 'text-slate-600 dark:text-slate-400';
  };

  // Week label
  const weekLabel = useMemo(() => {
    if (weekDates.length < 7) return '';
    const start = parseISO(weekDates[0]);
    const end = parseISO(weekDates[6]);
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
  }, [weekDates]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <TooltipProvider delayDuration={300}>
        <div className="space-y-6" data-testid="timesheet-page">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                {t('timesheet.title')}
              </h1>
              <p className="text-slate-500 mt-1">{isFreePlan ? 'Track and review your weekly timesheets' : t('timesheet.subtitle')}</p>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
              <button
                onClick={() => setViewMode('weekly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'weekly' 
                    ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white' 
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-200'
                }`}
                data-testid="view-weekly"
              >
                <Calendar className="w-4 h-4 inline-block mr-1.5" />
                {t('timesheet.weekly')}
              </button>
              <button
                onClick={() => setViewMode('monthly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'monthly' 
                    ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white' 
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-200'
                }`}
                data-testid="view-monthly"
              >
                <FileText className="w-4 h-4 inline-block mr-1.5" />
                {t('timesheet.monthly')}
              </button>
            </div>
          </div>

          {/* Status and Rejection Note */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {!isFreePlan && <StatusBadge status={timesheet?.status} large />}
              
              {/* Week Navigation */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigateWeek(-1)}
                  className="h-9 w-9 rounded-xl"
                  data-testid="prev-week"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300 min-w-[180px] text-center">
                  {weekLabel}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigateWeek(1)}
                  className="h-9 w-9 rounded-xl"
                  data-testid="next-week"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                {!isCurrentWeek && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToToday}
                    className="h-9 rounded-xl ml-2"
                    data-testid="today-btn"
                  >
                    Today
                  </Button>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {!isFreePlan && isEditable && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          onClick={() => canSubmitTimesheet.allowed ? setShowSubmitDialog(true) : toast.error(canSubmitTimesheet.reason)}
                          className={`h-10 ${canSubmitTimesheet.allowed ? 'btn-premium' : 'opacity-60 cursor-not-allowed bg-slate-400 hover:bg-slate-400 text-white'}`}
                          data-testid="submit-btn"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {timesheet?.status === 'rejected' ? 'Resubmit' : t('timesheet.submit')}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!canSubmitTimesheet.allowed && (
                      <TooltipContent>
                        <p>{canSubmitTimesheet.reason}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
              <Button
                variant="outline"
                onClick={() => handleExport('csv')}
                className="h-10 rounded-xl"
                data-testid="export-csv"
              >
                <Download className="w-4 h-4 mr-2" />
                {t('common.export')}
              </Button>
            </div>
          </div>

          {/* Rejection Note */}
          {!isFreePlan && timesheet?.status === 'rejected' && timesheet?.rejection_note && (
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-500/30 rounded-2xl p-4" data-testid="rejection-note">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">
                    Timesheet Rejected
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                    {timesheet.rejection_note}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Submission Day Restriction Banner */}
          {!isFreePlan && isEditable && !canSubmitTimesheet.allowed && (
            <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/30 rounded-2xl p-4" data-testid="submission-restricted-banner">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                    {t('approvals.submissionNotAvailable')}
                  </h3>
                  <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                    {canSubmitTimesheet.reason}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Timesheet Grid */}
          {viewMode === 'weekly' && (
            <div className="premium-card overflow-hidden" data-testid="timesheet-grid">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400 w-48">
                        {t('projects.title')} / {t('common.description')}
                      </th>
                      {weekDates.map((date, idx) => {
                        const dayDate = parseISO(date);
                        const weekend = isWeekendDate(date);
                        const holiday = getHolidayForDate(date);
                        const today = isTodayDate(date);
                        const future = isFutureDate(date);
                        const headerClass = getHeaderClass(date);
                        
                        return (
                          <th 
                            key={date} 
                            className={`text-center py-4 px-2 text-sm font-semibold w-24 ${headerClass}`}
                          >
                            <Tooltip>
                              <TooltipTrigger className="cursor-default">
                                <div className={today ? 'font-bold' : ''}>{format(dayDate, 'EEE')}</div>
                                <div className={`text-xs font-normal ${future && !weekend ? 'text-slate-400' : ''}`}>
                                  {format(dayDate, 'MMM d')}
                                </div>
                                {holiday && (
                                  <div className="text-[10px] text-red-500 dark:text-red-400 truncate mt-0.5">
                                    {holiday.name}
                                  </div>
                                )}
                              </TooltipTrigger>
                              {holiday && (
                                <TooltipContent>
                                  <p className="text-xs">{holiday.name} - Office closed</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </th>
                        );
                      })}
                      <th className="text-center py-4 px-3 text-sm font-semibold text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 w-20">
                        {t('common.total')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Project rows */}
                    {projects.map((project) => (
                      <tr key={project.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:bg-slate-700/50 dark:hover:bg-slate-800/30">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: project.color }}
                            />
                            <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
                              {project.name}
                            </span>
                          </div>
                        </td>
                        {weekDates.map((date) => {
                          const cellHours = getCellHours(project.id, date);
                          const isEditing = editingCell?.projectId === project.id && editingCell?.date === date;
                          const weekend = isWeekendDate(date);
                          const holiday = getHolidayForDate(date);
                          const canEdit = isCellEditable(date);
                          const isHovered = hoveredCell === `${project.id}-${date}`;
                          const bgClass = getCellBackgroundClass(date, cellHours > 0);
                          
                          return (
                            <td 
                              key={date} 
                              className={`py-2 px-1 text-center ${bgClass}`}
                              onMouseEnter={() => canEdit && setHoveredCell(`${project.id}-${date}`)}
                              onMouseLeave={() => setHoveredCell(null)}
                            >
                              {isEditing ? (
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  value={cellValue}
                                  onChange={(e) => {
                                    // Allow only numbers and decimal point
                                    const val = e.target.value.replace(/[^0-9.]/g, '');
                                    setCellValue(val);
                                  }}
                                  onBlur={handleCellSave}
                                  onKeyDown={handleCellKeyDown}
                                  autoFocus
                                  placeholder="0"
                                  className="w-16 h-8 text-center text-sm mx-auto [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  data-testid={`cell-input-${project.id}-${date}`}
                                />
                              ) : (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => handleCellClick(project.id, date)}
                                      disabled={!canEdit}
                                      className={`w-16 h-8 rounded-lg text-sm font-medium transition-all relative ${
                                        cellHours > 0 
                                          ? 'bg-blue-50 dark:bg-blue-900/10 text-blue-700 dark:text-blue-300' 
                                          : weekend || holiday
                                            ? 'text-slate-300 dark:text-slate-600 dark:text-slate-300'
                                            : 'text-slate-400 hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-800'
                                      } ${!canEdit ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:ring-2 hover:ring-blue-200'}`}
                                      data-testid={`cell-${project.id}-${date}`}
                                    >
                                      {formatHours(cellHours)}
                                      {/* Pencil icon on hover */}
                                      {isHovered && canEdit && (
                                        <Pencil className="w-3 h-3 absolute top-0.5 right-0.5 text-slate-400" />
                                      )}
                                    </button>
                                  </TooltipTrigger>
                                  {!canEdit && !weekend && !holiday && (
                                    <TooltipContent>
                                      <p className="text-xs">Timesheet submitted. Contact your manager to make changes.</p>
                                    </TooltipContent>
                                  )}
                                  {(weekend || holiday) && (
                                    <TooltipContent>
                                      <p className="text-xs">{holiday ? holiday.name : 'Weekend'} - Not editable</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              )}
                            </td>
                          );
                        })}
                        <td className="py-3 px-3 text-center bg-slate-50 dark:bg-slate-800/50">
                          <span className="text-sm font-semibold text-slate-900 dark:text-white">
                            {formatHours(projectTotals[project.id])}
                          </span>
                        </td>
                      </tr>
                    ))}
                    
                    {/* No Project row - always shown if there are hours tracked without a project */}
                    {(projects.length === 0 || projectTotals['no-project'] > 0) && (
                    <tr className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:bg-slate-700/50 dark:hover:bg-slate-800/30">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full flex-shrink-0 bg-slate-400" />
                          <span className="text-sm font-medium text-slate-500 italic">
                            No Project
                          </span>
                        </div>
                      </td>
                      {weekDates.map((date) => {
                        const cellHours = getCellHours('no-project', date);
                        const isEditing = editingCell?.projectId === 'no-project' && editingCell?.date === date;
                        const weekend = isWeekendDate(date);
                        const holiday = getHolidayForDate(date);
                        const canEdit = isCellEditable(date);
                        const isHovered = hoveredCell === `no-project-${date}`;
                        const bgClass = getCellBackgroundClass(date, cellHours > 0);
                        
                          return (
                          <td 
                            key={date} 
                            className={`py-2 px-1 text-center ${bgClass}`}
                            onMouseEnter={() => canEdit && setHoveredCell(`no-project-${date}`)}
                            onMouseLeave={() => setHoveredCell(null)}
                          >
                            {isEditing ? (
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={cellValue}
                                onChange={(e) => {
                                  // Allow only numbers and decimal point
                                  const val = e.target.value.replace(/[^0-9.]/g, '');
                                  setCellValue(val);
                                }}
                                onBlur={handleCellSave}
                                onKeyDown={handleCellKeyDown}
                                autoFocus
                                placeholder="0"
                                className="w-16 h-8 text-center text-sm mx-auto [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                data-testid={`cell-input-no-project-${date}`}
                              />
                            ) : (
                              <button
                                onClick={() => handleCellClick('no-project', date)}
                                disabled={!canEdit}
                                className={`w-16 h-8 rounded-lg text-sm font-medium transition-all relative ${
                                  cellHours > 0 
                                    ? 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300' 
                                    : weekend || holiday
                                      ? 'text-slate-300 dark:text-slate-600 dark:text-slate-300'
                                      : 'text-slate-400 hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-800'
                                } ${!canEdit ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:ring-2 hover:ring-slate-200'}`}
                                data-testid={`cell-no-project-${date}`}
                              >
                                {formatHours(cellHours)}
                                {/* Pencil icon on hover */}
                                {isHovered && canEdit && (
                                  <Pencil className="w-3 h-3 absolute top-0.5 right-0.5 text-slate-400" />
                                )}
                              </button>
                            )}
                          </td>
                        );
                      })}
                      <td className="py-3 px-3 text-center bg-slate-50 dark:bg-slate-800/50">
                        <span className="text-sm font-semibold text-slate-500">
                          {formatHours(projectTotals['no-project'] || 0)}
                        </span>
                      </td>
                    </tr>
                    )}

                    {/* Daily Totals Row */}
                    <tr className="bg-slate-50 dark:bg-slate-800/50 border-t-2 border-slate-200 dark:border-slate-700">
                      <td className="py-4 px-4">
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                          {t('common.total')} {t('timeTracker.today')}
                        </span>
                      </td>
                      {weekDates.map((date) => {
                        const total = dailyTotals[date];
                        const weekend = isWeekendDate(date);
                        const holiday = getHolidayForDate(date);
                        const today = isTodayDate(date);
                        const future = isFutureDate(date);
                        const pastWorking = isPastWorkingDay(date) && !holiday;
                        
                        // Determine display value and styling
                        let displayValue;
                        let textClass;
                        let bgClass = '';
                        
                        if (total > 0) {
                          // Has hours tracked - show them even on holidays/weekends
                          displayValue = formatHours(total);
                          textClass = total >= 8 
                            ? 'text-emerald-600 dark:text-emerald-400' 
                            : 'text-slate-900 dark:text-white';
                          if (today) bgClass = 'bg-blue-50 dark:bg-blue-900/30/50 dark:bg-blue-900/5';
                          else if (holiday) bgClass = 'bg-red-50 dark:bg-red-900/30/50 dark:bg-red-900/5';
                          else if (weekend) bgClass = 'bg-slate-100 dark:bg-slate-700/50 dark:bg-slate-800/30';
                        } else if (weekend || holiday) {
                          // Weekend/Holiday with no hours: show "-" in grey
                          displayValue = '-';
                          textClass = 'text-slate-400 dark:text-slate-600 dark:text-slate-300';
                          bgClass = weekend ? 'bg-slate-100 dark:bg-slate-700/50 dark:bg-slate-800/30' : 'bg-red-50 dark:bg-red-900/30/50 dark:bg-red-900/5';
                        } else if (pastWorking) {
                          // Past working day with 0h: orange warning
                          displayValue = '0h 0m';
                          textClass = 'text-amber-600 dark:text-amber-400';
                          bgClass = 'bg-amber-50 dark:bg-amber-900/30/50 dark:bg-amber-900/5';
                        } else {
                          // Today or future with 0h: grey, normal
                          displayValue = '-';
                          textClass = 'text-slate-400 dark:text-slate-500';
                          if (today) bgClass = 'bg-blue-50 dark:bg-blue-900/30/50 dark:bg-blue-900/5';
                        }
                        
                        return (
                          <td key={date} className={`py-4 px-2 text-center ${bgClass}`}>
                            <span className={`text-sm font-bold ${textClass}`}>
                              {displayValue}
                            </span>
                          </td>
                        );
                      })}
                      <td className="py-4 px-3 text-center bg-blue-50 dark:bg-blue-900/20">
                        <span className="text-base font-bold text-blue-700 dark:text-blue-300">
                          {formatGrandTotal(grandTotal)}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Monthly View */}
          {viewMode === 'monthly' && (
            <MonthlyView 
              api={api} 
              currentWeekStart={currentWeekStart}
              onWeekSelect={(weekStart) => {
                setCurrentWeekStart(weekStart);
                setViewMode('weekly');
              }}
            />
          )}

          {/* Previous Timesheets */}
          <Collapsible open={showHistorySection} onOpenChange={setShowHistorySection}>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white dark:hover:text-white transition-colors" data-testid="show-history">
              <History className="w-4 h-4" />
              <span>Previous Timesheets</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showHistorySection ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-4 space-y-2" data-testid="history-list">
                {history.filter(h => h.week_start !== currentWeekStart).slice(0, 8).map((ts) => {
                  const startDate = parseISO(ts.week_start);
                  const endDate = addDays(startDate, 6);
                  return (
                    <button
                      key={ts.week_start}
                      onClick={() => {
                        setCurrentWeekStart(ts.week_start);
                        setShowHistorySection(false);
                      }}
                      className="w-full flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-800 transition-colors text-left"
                      data-testid={`history-${ts.week_start}`}
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-semibold text-slate-900 dark:text-white">
                          {ts.total_hours?.toFixed(1) || 0}h
                        </span>
                        {!isFreePlan && <StatusBadge status={ts.status} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Submit Dialog */}
        <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
          <DialogContent className="sm:max-w-md rounded-2xl" data-testid="submit-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl">
                {timesheet?.status === 'rejected' ? t('timesheet.resubmit') : t('timesheet.submitTimesheet')}
              </DialogTitle>
              <DialogDescription>
                Review your hours before submitting for approval
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Week */}
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                <span className="text-sm text-slate-500">Week</span>
                <span className="text-sm font-semibold text-slate-900 dark:text-white">{weekLabel}</span>
              </div>

              {/* Hours by Project */}
              <div className="space-y-2">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Hours by Project</span>
                {projects.filter(p => projectTotals[p.id] > 0).map(project => (
                  <div key={project.id} className="flex items-center justify-between p-2 pl-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: project.color }} />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{project.name}</span>
                    </div>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{formatHours(projectTotals[project.id])}h</span>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-500/20">
                <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">{t('approvals.totalHours')}</span>
                <span className="text-xl font-bold text-blue-700 dark:text-blue-300">{formatHours(grandTotal)}h</span>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowSubmitDialog(false)} className="rounded-xl">
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                className="btn-premium"
                data-testid="confirm-submit"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {timesheet?.status === 'rejected' ? 'Resubmit' : 'Submit'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TooltipProvider>
    </Layout>
  );
};

export default Timesheet;
